import { ReactNode } from "react"
import { UserAdviceButton } from "../../Components/UserAdvice/UserAdvice"
import { UserAdviceFormat, UserAdviceProps, UserAdviceRole } from "../../Components/UserAdvice/Params"
import { IAppState, ILog, IShip, IStatus, IStatusType } from "../../Types"
import type { IStopRequest } from "../../Types/Generated/LogbookApp/CurrentLogApi"
import { FocusViewState } from "../FocusView"
import { OverlayViewDefinition } from "../OverlayView"

// The user-advice core: the state the surrounding row keeps, the per-button instance
// an advice hands it, and the base class every advice extends. Split out of the old
// App/UserAdvice.tsx monolith (#1171) so the advice classes sit beside the dialogs
// they render, matching how the rest of Views/ is laid out.

export interface UserAdviceState {
  selected?: string;
  setSelected: (selected?: string) => void;
  recordStop?: (values: IStopRequest) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  data?: any;
}

export enum UserAdviceLocation {
  log = "log",
  day = "day",
  // The ship toolbar (#916), where the `monitor`-role advices live: things you
  // switch on and leave running, whose state outlives any one log. The log row and
  // day summary are for what needs attention *now*, so a monitor renders here and
  // nowhere else -- an open alert still reaches the row through `AlertAdvice`.
  toolbar = "toolbar",
}

export class UserAdviceInstance {
  id: string;
  index: number;
  level: number;
  props: UserAdviceProps;
  parent: UserAdvice;
  // Where this instance was asked for. Carried so the row can rank the buttons it is
  // about to render (#1171) -- the day summary spells its advices out, the crowded
  // current-log row does not.
  location: UserAdviceLocation;

  constructor(id: string, level: number, props: UserAdviceProps, parent: UserAdvice, index: number = 0, location: UserAdviceLocation = UserAdviceLocation.log) {
    this.id = id;
    this.index = index;
    this.level = level;
    this.props = props;
    this.parent = parent;
    this.location = location;
  }

  // Only the button: since #1171 an advice's dialog is NOT rendered beside it but
  // mounted by `UserAdviceOverlayView`, so the row stays a row of buttons and the
  // open dialog has a single host -- the same split the App and Ship hubs use.
  renderButton(state: UserAdviceState, format?: UserAdviceFormat): ReactNode {
    return <UserAdviceButton advice={this} format={format} state={state} />;
  }

  // This advice's overlay, as `OverlayView` wants it. `undefined` when the advice
  // can no longer describe itself (e.g. its alert closed while the dialog was open),
  // which the overlay host reads as "nothing to show".
  overlay(app: IAppState, state: UserAdviceState): OverlayViewDefinition | undefined {
    return this.parent.overlay(app, this, state);
  }
}

export class UserAdvice {
  // What job this advice does (#916), so a surface can offer just the advices it
  // is for -- the log row shows alerts and actions, the ship toolbar shows
  // monitors. Every subclass declares its own; `action` is the default because it
  // is the least privileged: a new advice that forgets to say shows up as an
  // ordinary prompt rather than silently claiming a monitor's toolbar slot.
  readonly role: UserAdviceRole = UserAdviceRole.action;
  feature?: string;

  constructor() {
  }

  getInstances(_app: IAppState, _location: UserAdviceLocation, _currentLog?: ILog): UserAdviceInstance[] {
    return [];
  }

  update(_app: IAppState, _state: FocusViewState, _currentLog?: ILog, _ship?: IShip) {
  }

  overlay(_app: IAppState, _advice: UserAdviceInstance, _state: UserAdviceState): OverlayViewDefinition | undefined {
    return undefined;
  }

  public static status(statuses: IStatus[], type: IStatusType, dayId: number): IStatus[] {
    return statuses.filter((s) => s.statusType == type && s.dayId == dayId);
  }
}

// What a `UserAdviceRole.monitor` advice must be able to answer (#916), so the ship
// toolbar can drive any of them without knowing which one it holds: switching a
// toggle ON opens `overlay()` to review the settings and activate, switching it OFF
// calls `deactivate()` immediately with no dialog (@almute's redesign).
//
// This is a contract, not a shared store, because the monitors are not uniformly
// backed: the anchor and depth watches are `IStatusType.monitor` statuses written
// through their own lifecycle routes, while navigation lights is a ship setting
// (`IShipSettings.lightsReminder`). Each implements these four over whatever backs
// it.
export interface UserAdviceMonitor {
  // Is this monitor running right now? Drives the toggle's on/off colour.
  isActive(): boolean;
  // Separate from activation: an enabled watch can lack the data it needs.
  hasProblem?(app: IAppState): boolean;
  // Can it be offered at all? Distinct from `isActive`: the anchor watch is only
  // meaningful while the ship is anchored or on a buoy, so it can be unavailable
  // and yet not running. A monitor that is always offerable returns whether there
  // is a current log to attach to.
  isAvailable(): boolean;
  // Switch on / off. Both go through the monitor's own write path -- the client
  // never composes the underlying status or setting write itself.
  activate(app: IAppState): Promise<void>;
  deactivate(app: IAppState): Promise<void>;
}

// Whether an advice implements the monitor contract, so a surface holding plain
// `UserAdvice[]` can narrow to it. Checks the methods rather than just the role:
// the role says what an advice is *for*, this says it can actually be driven, and
// an advice that declares `monitor` without implementing them is a bug the
// toolbar should not be the one to discover.
export function isUserAdviceMonitor(advice: UserAdvice): advice is UserAdvice & UserAdviceMonitor {
  const candidate = advice as Partial<UserAdviceMonitor>;
  return typeof candidate.isActive == "function"
    && typeof candidate.isAvailable == "function"
    && typeof candidate.activate == "function"
    && typeof candidate.deactivate == "function";
}
